import type { TFunction } from 'i18next';
import type { IPeriodTag } from '@carrierbridge/shared';
import type { UseFormReturn } from 'react-hook-form';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/Checkbox';
import { Input } from '@/components/ui/Input';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Button } from '@/components/ui/Button';
import type { QuoteFormValues } from './schema';

type RouteMode = 'direct' | 'from_site' | 'to_site';
type DisplayOption = { value: string; label: string; group?: string | null; defaultEnabled?: boolean };

/** Collapsible "details" block: postal codes, weight/palette conversion,
 *  period tags, consignee type and ancillary options. */
export function QuoteDetailsSection({
  t,
  open,
  onToggle,
  form,
  routeMode,
  kgPerUnit,
  setKgPerUnit,
  unitsPerPalette,
  setUnitsPerPalette,
  unitLabel,
  computedWeightKg,
  computedPalettes,
  periodTags,
  selectedTagIds,
  setSelectedTagIds,
  consigneeType,
  setConsigneeType,
  displayOptions,
  selectedOptions,
  setSelectedOptions,
}: {
  t: TFunction;
  open: boolean;
  onToggle: () => void;
  form: UseFormReturn<QuoteFormValues>;
  routeMode: RouteMode;
  kgPerUnit: number | '';
  setKgPerUnit: (v: number | '') => void;
  unitsPerPalette: number | '';
  setUnitsPerPalette: (v: number | '') => void;
  unitLabel: string;
  computedWeightKg: number | null;
  computedPalettes: number | null;
  periodTags: IPeriodTag[];
  selectedTagIds: string[];
  setSelectedTagIds: React.Dispatch<React.SetStateAction<string[]>>;
  consigneeType: string;
  setConsigneeType: (v: string) => void;
  displayOptions: DisplayOption[];
  selectedOptions: string[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<string[]>>;
}) {
  const { register } = form;

  return (
    <div>
      {/* eslint-disable-next-line no-restricted-syntax -- en-tête de section repliable (cible cliquable, pas un bouton d'action) */}
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="w-full flex items-center justify-between py-1 group"
      >
        <span className="text-xs font-medium text-subtle group-hover:text-muted transition-colors">
          {t('quote.details')}
        </span>
        <ChevronRight className={cn(
          'h-3.5 w-3.5 text-subtle transition-transform duration-200',
          open && 'rotate-90',
        )} />
      </button>

      {open && (
        <div className="mt-3 space-y-4">
          {/* CP fields */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {routeMode !== 'from_site' && (
              <Input
                label={t('quote.postalCodeOrigin')}
                placeholder={t('quote.postalCodeOriginPlaceholder')}
                {...register('originPostalCode')}
              />
            )}
            {routeMode !== 'to_site' && (
              <Input
                label={t('quote.postalCodeDest')}
                placeholder={t('quote.postalCodeDestPlaceholder')}
                {...register('destPostalCode')}
              />
            )}
          </div>

          {/* Weight & palette conversion */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Input
              label={`1 ${unitLabel} = kg`}
              type="number"
              step="0.01"
              min="0"
              value={kgPerUnit}
              onChange={(e) => setKgPerUnit(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="—"
            />
            <Input
              label={`1 pal = ${unitLabel}`}
              type="number"
              step="1"
              min="1"
              value={unitsPerPalette}
              onChange={(e) => setUnitsPerPalette(e.target.value === '' ? '' : Number(e.target.value))}
              placeholder="600"
            />
          </div>

          {/* Computed values */}
          {(computedWeightKg !== null || computedPalettes !== null) && (
            <div className="flex items-center gap-4 text-2xs text-subtle">
              {computedWeightKg !== null && (
                <span>{t('quote.weight')} <span className="font-medium text-muted">{Math.round(computedWeightKg * 100) / 100} kg</span></span>
              )}
              {computedPalettes !== null && (
                <>
                  <span className="h-3 w-px bg-border" />
                  <span>{t('quote.pal')} <span className="font-medium text-muted">{Math.round(computedPalettes * 100) / 100}</span></span>
                </>
              )}
            </div>
          )}

          {/* Period tags */}
          {periodTags.length > 0 && (
            <div>
              <p className="mb-1.5 text-[13px] font-medium text-text">{t('quote.periodTags')}</p>
              <div className="flex flex-wrap gap-1.5">
                {periodTags.map((tag) => {
                  const selected = selectedTagIds.includes(tag.id);
                  return (
                    <Button
                      key={tag.id}
                      type="button"
                      size="xs"
                      variant={selected ? 'secondary' : 'outline'}
                      aria-pressed={selected}
                      className={cn(selected && 'border-accent/40 bg-accent-soft text-accent hover:bg-accent-soft')}
                      onClick={() => setSelectedTagIds((prev) => prev.includes(tag.id) ? prev.filter((v) => v !== tag.id) : [...prev, tag.id])}
                    >
                      {tag.label}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Consignee type */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="whitespace-nowrap text-[13px] font-medium text-text">{t('quote.consignee')}</span>
            <SegmentedControl
              size="sm"
              aria-label={t('quote.consignee')}
              value={consigneeType}
              onChange={setConsigneeType}
              options={[
                { value: '', label: t('quote.consigneeAll') },
                { value: 'B2B', label: 'B2B' },
                { value: 'B2C', label: 'B2C' },
              ]}
            />
          </div>

          {/* Options checkboxes */}
          {displayOptions.length > 0 && (
            <div>
              <p className="mb-1.5 text-[13px] font-medium text-text">{t('quote.optionsAndSupplements')}</p>
              <div className="grid grid-cols-1 gap-x-3 gap-y-1.5 sm:grid-cols-2">
                {displayOptions.map((opt) => (
                  <Checkbox
                    key={opt.value}
                    label={opt.label}
                    checked={selectedOptions.includes(opt.value)}
                    onChange={(e) => {
                      if (e.target.checked) setSelectedOptions((prev) => [...prev, opt.value]);
                      else setSelectedOptions((prev) => prev.filter((v) => v !== opt.value));
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
